import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from './Icons';

interface SheetProps {
  id?: string;
  className?: string;
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
  
  showTitle?: boolean;
  
  closeOnBackdrop?: boolean;
}


export function Sheet({ id, className = '', open, onClose, title, children, wide = false, showTitle = false, closeOnBackdrop = true }: SheetProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = (
    <button type="button" className="icon-button sheet__close" onClick={onClose} aria-label={t('actions.close')}>
      <CloseIcon />
    </button>
  );

  return (
    <dialog
      id={id}
      ref={ref}
      className={`sheet${wide ? ' sheet--wide' : ''}${className ? ` ${className}` : ''}`}
      aria-label={title}
      onClose={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onCancel={(event) => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        if (closeOnBackdrop) onClose();
      }}
      onKeyDown={(event) => {
        if (!closeOnBackdrop && event.key === 'Escape') onClose();
      }}
      onClick={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet__panel">
        <span className="sheet__handle" aria-hidden="true" />
        {showTitle ? (
          <div className="sheet__header">
            <h2 className="sheet__title">{title}</h2>
            {close}
          </div>
        ) : (
          <div className="sheet__float">{close}</div>
        )}
        {open && children}
      </div>
    </dialog>
  );
}
